import { Ng2StateDeclaration, StatesModule } from '@uirouter/angular';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiAbstractStateComponent } from '@fi-shared/fi-state-proxy/fi-abstract-state.component';
import { Transition } from '@uirouter/core';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiChecklist2TemplateListComponent } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template-list/fi-checklist2-template-list.component';
import { ObjectTypes } from '@fi-shared/types';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiChecklist2EditComponent } from '@fi-shared/checklist/fi-checklist2-edit/fi-checklist2-edit.component';
import {
    ChecklistV2AuditEdit_accessRightFilterCollection,
    ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn,
} from '@fi-app/checklist/checklist-v2/checklist-v2-audit.states';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { ChecklistV2Edit_isChatAvailable } from '@fi-app/checklist/checklist-v2/checklist-v2.states';
import {
    ChecklistV2Add_templateFn,
    ChecklistV2_EditCheckpointResolve,
    ChecklistV2Template_AddCheckpointSectionsResolve,
    ChecklistV2TemplateEdit_getTemplateFn,
    ChecklistV2_templateCopyAddCheckpointSectionsResolve,
    ChecklistV2Add_templateCopyChecklistFn,
    onChecklistEditorStateExit,
    ChecklistV2_EditCheckpointSectionsResolve,
} from '@fi-app/checklist/checklist-v2/checklist-v2-shared-resolvers';
import { FiCheckpointEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/checkpoint-edit.component';
import { FiChecklist2CheckpointsSectionEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoints-section-edit.component';

const ChecklistV2AuditTemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template._name,
    url: '/Template',
    abstract: true,
    component: FiAbstractStateComponent,
};

const ChecklistV2AuditTemplateListState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiChecklist2TemplateListComponent,
    reloadOnSearch: false,
    resolve: {
        auditMode: [() => true],
    },
};

const accessRightFilterCollectionResolveFn = [
    AccessRightService,
    (accessRightService: AccessRightService) => {
        return accessRightService.getAccessRightsForPublicationByObjectType(ObjectTypes.Checklist2AuditTemplate);
    },
];

const ChecklistV2AuditTemplateAddState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Add._name,
    url: '/Add',
    params: {
        checklistTemplate: null,
        isTemplateBased: false,
        templateMode: true,
        isAudit: true,
    },
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    resolve: {
        checklist: ChecklistV2Add_templateFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn],
        templateMode: [() => true],
        isTemplateBased: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).isTemplateBased,
        ],
        auditMode: [() => true],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2AuditTemplateEditState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Edit._name,
    url: '/{id:[0-9]+}/Edit',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: true,
        isTemplateBased: false,
        templateMode: true,
    },
    resolve: {
        checklist: ChecklistV2TemplateEdit_getTemplateFn,
        accessRightFilterCollection: accessRightFilterCollectionResolveFn,
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn],
        templateMode: [() => true],
        auditMode: [() => true],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2AuditFromTemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Copy._name,
    url: '/{id:[0-9]+}/Copy',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: true,
        isTemplateBased: true,
        templateMode: false,
    },
    resolve: {
        templateId: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => {
                return coerceNumberProperty(fiStateService.getParams(tr).id);
            },
        ],
        isTemplateBased: [() => true],
        accessRightFilterCollection: [AccessRightService, ChecklistV2AuditEdit_accessRightFilterCollection],
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn],
        auditMode: [() => true],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        checklist: ChecklistV2Add_templateCopyChecklistFn,
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2AuditFromTemplateAddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Copy.AddCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: false,
        isTemplateBased: true,
        checkpoint: null,
    },
    resolve: ChecklistV2_templateCopyAddCheckpointSectionsResolve,
};

const ChecklistV2AuditFromTemplateAddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Copy.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: false,
        isTemplateBased: true,
        checkpoint: null,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2TemplateEditCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Edit.EditCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: true,
        isTemplateBased: false,
    },
    resolve: ChecklistV2_EditCheckpointSectionsResolve,
};

const ChecklistV2AuditTemplateEditCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Edit.EditCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: true,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2AuditTemplateAddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Add.AddCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: true,
        isTemplateBased: false,
        checkpoint: null,
    },
    resolve: ChecklistV2Template_AddCheckpointSectionsResolve,
};

const ChecklistV2AuditTemplateAddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Template.Add.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: true,
        isTemplateBased: false,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

export const ChecklistV2AuditTemplateStates: StatesModule = {
    states: [
        ChecklistV2AuditTemplateState,
        ChecklistV2AuditTemplateListState,
        ChecklistV2AuditTemplateAddState,
        ChecklistV2AuditTemplateEditState,
        ChecklistV2AuditFromTemplateState,
        ChecklistV2AuditFromTemplateAddCheckpointSectionsEditCheckpointState,
        ChecklistV2AuditTemplateAddCheckpointSectionsEditCheckpointState,
        ChecklistV2TemplateEditCheckpointSectionsState,
        ChecklistV2AuditTemplateEditCheckpointSectionsEditCheckpointState,
        ChecklistV2AuditTemplateAddCheckpointSectionsState,
        ChecklistV2AuditFromTemplateAddCheckpointSectionsState,
    ],
};
